import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchPrograms } from "../../redux/program/programActions";
import { viewClassWiseResultAnalysis } from "../../services/ResultService";
import Select from "../../components/Select/SearchableSelect";
import { FaUsers, FaChevronDown, FaChevronUp } from "react-icons/fa";
import Button from "../../components/Button/Button";
import Loader from "../../components/Loader/Loader";
import * as XLSX from "xlsx";

const ResultAnalysisClassWise = () => {
  const [program_id, setProgramID] = useState("");
  const [data, setData] = useState([]);
  const [loader, setLoader] = useState(false);
  const [expandedTables, setExpandedTables] = useState({});
  const [exam_type, setExamType] = useState("");

  const dispatch = useDispatch();
  const programData = useSelector((state) => state.program.programs.data);

  const changeHandler = (e) => {
    const { name, value } = e.target;
    if (name === "program_id") {
      setProgramID(value);
    } else if (name === "exam_type") {
      setExamType(value);
    }
  };

  const fetchResultClassWise = async () => {
    try {
      setLoader(true);
      const response = await viewClassWiseResultAnalysis(program_id, exam_type);
      if (response.status === 200) {
        const resultData = response.data.data;
        setData(resultData);
        // Initialize all tables as collapsed
        const expandedState = {};
        resultData.forEach((_, idx) => {
          expandedState[idx] = false;
        });
        setExpandedTables(expandedState);
      }
    } catch (error) {
      console.error("Error fetching class-wise result analysis:", error);
    } finally {
      setLoader(false);
    }
  };

  const toggleTable = (index) => {
    setExpandedTables((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  const resultClassWiseDownload = () => {
    if (data.length === 0) {
      return;
    }
    const workbook = XLSX.utils.book_new();
    data.forEach((subjectData) => {
      const worksheetData = [
        [
          "Teacher Name",
          "Class Name",
          "Subject Name",
          "Average Marks",
          "Student Passed",
          "Student Failed",
          "Total Students",
          "Student Name",
          "Marks Obtained",
          "Max Marks",
        ],
        ...subjectData.students.map((student) => [
          subjectData.teacher_name,
          subjectData.class_name,
          subjectData.subject_name,
          subjectData.average_marks,
          subjectData.students_passed,
          subjectData.students_failed,
          subjectData.total_students,
          student.student_name,
          student.marks_obtained,
          student.max_marks,
        ]),
      ];

      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
      XLSX.utils.book_append_sheet(
        workbook,
        worksheet,
        `${subjectData.subject_name}`
      );
    });

    XLSX.writeFile(workbook, "ResultAnalysisClassWise.xlsx");
  };

  useEffect(() => {
    dispatch(fetchPrograms());
  }, [dispatch]);

  return (
    <div className="card">
      <div className="card-header">
        <div className="d-flex justify-content-between align-items-center">
          <div className="main-title-page page-title d-flex align-items-center">
            <div className="mx-2">
              <FaUsers />
            </div>
            <span>Result Analysis</span>
          </div>
        </div>
      </div>

      <div className="row card-body">
        <div className="col-md-4">
          <Select
            name="program_id"
            placeholder="Select Class"
            onChangeHandler={(e) => setProgramID(e.target.value)}
            options={programData}
            value={program_id}
          />
        </div>
        <div className="col-md-4">
          <Select
            name="exam_type"
            placeholder="Select Exam Type"
            onChangeHandler={changeHandler}
            options={[
              { id: "term-1", name: "Term-1" },
              { id: "term-2", name: "Term-2" },
            ]}
            value={exam_type}
          />
        </div>
        <div className="col-md-4">
          <Button
            className="btn btn-primary mx-2"
            onClick={fetchResultClassWise}
          >
            View
          </Button>
          <Button className="btn btn-success" onClick={resultClassWiseDownload}>
            Download Excel
          </Button>
        </div>

        {loader ? (
          <Loader />
        ) : (
          <div className="mt-3">
            {data.length === 0 ? (
              <div className="d-flex justify-content-center">
                <h5>No data found</h5>
              </div>
            ) : (
              data.map((subjectData, index) => (
                <div key={index} className="mb-4 p-3 border rounded shadow-sm">
                  <div
                    className="d-flex justify-content-between align-items-center cursor-pointer"
                    onClick={() => toggleTable(index)}
                  >
                    <div className="w-100">
                      <div className="row">
                        <div className="col-md-3">
                          <strong>Class: </strong>
                          {subjectData.class_name}
                        </div>
                        <div className="col-md-3">
                          <strong>Subject: </strong>
                          {subjectData.subject_name}
                        </div>
                        <div className="col-md-3">
                          <strong>Teacher: </strong>
                          {subjectData.teacher_name}
                        </div>
                      </div>
                      <div className="row mt-2">
                        <div className="col-md-3">
                          <strong>Average: </strong>
                          {subjectData.average_marks}
                        </div>
                        <div className="col-md-3">
                          <strong>Pass %: </strong>
                          {subjectData.pass_percentage}%
                        </div>
                        <div className="col-md-3">
                          <strong>Students: </strong>
                          {subjectData.total_students}
                        </div>
                      </div>
                    </div>
                    <div className="ms-3">
                      {expandedTables[index] ? (
                        <FaChevronUp className="text-muted" />
                      ) : (
                        <FaChevronDown className="text-muted" />
                      )}
                    </div>
                  </div>

                  {expandedTables[index] && (
                    <div className="mt-3 animate__animated animate__fadeIn">
                      <table className="table table-bordered table-hover table-sm">
                        <thead className="table-light">
                          <tr>
                            <th>Student Name</th>
                            <th className="text-center">Marks Obtained</th>
                            <th className="text-center">Max Marks</th>
                          </tr>
                        </thead>
                        <tbody>
                          {subjectData.students.map((student, idx) => (
                            <tr key={idx}>
                              <td>{student.student_name}</td>
                              <td className="text-center">
                                {student.marks_obtained}
                              </td>
                              <td className="text-center">
                                {student.max_marks}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ResultAnalysisClassWise;
